import { useState, type FC } from 'react';
import { Modal, Space, Select } from '@g2u/ui-kit';
import { DefaultOptionType } from 'antd/es/select';
import { observer } from 'mobx-react-lite';
import { Cascader, Form } from 'antd';
import { isArray } from 'lodash';

import { JsonData, type JsonDataItem, type WithOptionalDataTest } from 'types';
import { validateFormSilently, useDiscountOptions } from 'helpers';
import styles from './add-discount-modal.module.scss';

type Props = {
  isOpen: boolean;
  discountList?: JsonData;
  approversList?: DefaultOptionType[] | undefined;
  selectedDiscounts: JsonData;
  reasons?: DefaultOptionType[] | undefined;
  onOk: (product: JsonDataItem) => void;
  onCancel: () => void;
} & WithOptionalDataTest;

export const AddDiscountModal: FC<Props> = observer((props) => {
  const { isOpen, onOk, onCancel, discountList, approversList, reasons, selectedDiscounts, dataTestId } = props;
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [showReason, setShowReason] = useState<boolean>(false);
  const [form] = Form.useForm();
  const { discountOptions } = useDiscountOptions(
    discountList,
    selectedDiscounts
      .filter((selectedDiscount) => selectedDiscount.details.type === 'Customer')
      .map(({ discountId }) => discountId),
  );

  const handleFormChange = (formData: JsonDataItem) => {
    // autoselect approver if available
    if (formData.discount) {
      const approvedByInfo = discountList?.find(
        (discount) => discount.customer_discount_id === formData.discount[1],
      )?.approved_by_info;
      approvedByInfo && approvedByInfo !== -1 && form.setFieldValue('approver', approvedByInfo.ID?.toString());
    }
    validateFormSilently(form, setIsFormValid);
  };

  const handleFormSubmit = (formData: JsonDataItem | null) => {
    const discountId = formData?.discount?.[1]; // Cascader return an array
    const discountData = discountList?.find((discount) => discount.customer_discount_id === discountId);
    onOk({
      discountId: discountId,
      reasonId: formData?.reason,
      approverId: formData?.approver,
      label: discountData?.discount_desc,
      amount: discountData?.amount,
      type: discountData?.discount_type,
      details: discountData,
    });
  };

  return (
    <Modal.$
      open={isOpen}
      destroyOnClose
      maskClosable={false}
      title={'Add Discount'}
      okText="Add"
      onOk={() => {
        form.submit();
      }}
      onCancel={onCancel}
      okButtonProps={{
        loading: false,
        disabled: !isFormValid,
      }}
      className={styles.addDiscountModal}
      dataTestId={dataTestId ?? 'add-discount-modal'}
    >
      <Form
        form={form}
        name="add-discount-form"
        layout="horizontal"
        onValuesChange={handleFormChange}
        onFinish={handleFormSubmit}
        colon={false}
        preserve={false}
      >
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle} fullWidth>
          <Form.Item name="discount" colon={true} label="Discount" required rules={[{ required: true }]}>
            <Cascader
              options={discountOptions as any}
              placeholder="Select"
              changeOnSelect={false}
              displayRender={(labels: string[]) => labels[labels.length - 1]}
              onChange={(val) => {
                isArray(val) &&
                  setShowReason(
                    !!discountList?.find((discount) => discount.customer_discount_id === val[1])?.reason_extra,
                  );
              }}
            />
          </Form.Item>

          <Select.$
            placeholder="Select"
            optionFilterProp="children"
            className={styles.addDiscountModal__select}
            showSearch
            name="approver"
            required
            rules={[{ required: true }]}
            label="Approved by"
            options={approversList}
            fullWidth
            colon={true}
          />

          {showReason && (
            <Select.$
              placeholder="Select"
              optionFilterProp="children"
              className={styles.addDiscountModal__select}
              showSearch
              name="reason"
              required
              rules={[{ required: true }]}
              label="Reason"
              options={reasons}
              fullWidth
              colon={true}
            />
          )}
        </Space.$>
      </Form>
    </Modal.$>
  );
});
